/**
 * ColleaguePicker — searchable async picker over /v1/me/colleagues.
 *
 * Used by:
 *   • WorkerSubmitRequest CC field (multi-select)
 *   • WorkerSubmitRequest `worker_reference` form fields (single-select,
 *     includeSelf — the requester can be the person a form names)
 *   • WorkerApprovals Forward dialog (single-select)
 *
 * Design constraints (svdy-rules §10.5 single source of truth):
 *   • One async-search hook so both surfaces share debounce / loading
 *     state / option-shape conventions.
 *   • One renderOption helper so the dropdown row chrome (avatar +
 *     name + position·department) matches across surfaces.
 *   • Two thin component wrappers (Single / Multi) so callers stay
 *     ergonomic without resorting to discriminated-union typing
 *     gymnastics on a generic Autocomplete.
 *
 * Backend contract: /me/colleagues requires `q` (min_length=1); we
 * disable client-side filtering (`filterOptions={(x) => x}`) because
 * the server is the source of truth — Postgres ilike beats client
 * substring match on Unicode + full-name concat ('John Doe' vs
 * first_name='John' last_name='Doe').
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete, TextField, Avatar, Chip, Box, Typography, CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { meApi, type MeColleague } from '../../api/me';

const DEBOUNCE_MS = 250;
const MAX_RESULTS = 20;

/**
 * Async search hook — caller wires inputValue ↔ Autocomplete's
 * onInputChange. Returns a stable reference on inputValue so the
 * debounced effect doesn't re-fire on unrelated re-renders.
 *
 * `includeSelf` is passed straight through to the endpoint. Default OFF, which
 * is right for CC and Forward; a `worker_reference` form field turns it ON
 * because the person filing an Offboarding may be its subject.
 */
export function useColleagueSearch(includeSelf = false) {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<MeColleague[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const q = inputValue.trim();
    if (q.length < 1) {
      setOptions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = (await meApi.colleagues.search(q, MAX_RESULTS, includeSelf)) as MeColleague[];
        setOptions(Array.isArray(r) ? r : []);
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [inputValue, includeSelf]);

  return { inputValue, setInputValue, options, loading };
}

/**
 * Dropdown row: avatar + name + (position · department) caption.
 * Compact (svdy-ui §2 Apple-style compactness): row height stays
 * within ~36px; secondary line is 0.65rem so the option list shows
 * 6+ rows in the standard MUI popper height.
 */
function renderColleagueOption(
  props: React.HTMLAttributes<HTMLLIElement> & { key?: React.Key },
  opt: MeColleague,
) {
  const meta = [opt.position, opt.department].filter(Boolean).join(' · ');
  // MUI passes `key` inside props; React 18 wants it spread separately
  // to avoid the "key prop spread" warning.
  const { key, ...rest } = props;
  return (
    <li {...rest} key={key ?? opt.worker_id}>
      <Avatar
        src={opt.avatar_url ?? undefined}
        sx={{ width: 26, height: 26, mr: 1.25, fontSize: '0.7rem', bgcolor: 'primary.main' }}
      >
        {opt.name?.charAt(0) ?? '?'}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 500, lineHeight: 1.2 }} noWrap>
          {opt.name}
        </Typography>
        {meta && (
          <Typography
            variant="caption"
            sx={{ display: 'block', color: 'text.secondary', fontSize: '0.65rem', lineHeight: 1.1 }}
            noWrap
          >
            {meta}
          </Typography>
        )}
      </Box>
    </li>
  );
}

const labelOf = (o: MeColleague) => o.name;
const eqOf = (a: MeColleague, b: MeColleague) => a.worker_id === b.worker_id;

interface CommonProps {
  label?: string;
  placeholder?: string;
  helperText?: string;
  required?: boolean;
  size?: 'small' | 'medium';
  disabled?: boolean;
  /** Worker IDs to exclude from results client-side. Useful when CC
   *  shouldn't include the requester themselves, or the Forward
   *  picker shouldn't include the current approver (server already
   *  excludes self, but we filter again as defense-in-depth). */
  excludeIds?: string[];
  /** Offer the requester themselves. See `useColleagueSearch`. */
  includeSelf?: boolean;
}

interface SingleProps extends CommonProps {
  value: MeColleague | null;
  onChange: (v: MeColleague | null) => void;
}

interface MultiProps extends CommonProps {
  value: MeColleague[];
  onChange: (v: MeColleague[]) => void;
}

/**
 * Single-colleague picker (Forward dialog use case).
 */
export const ColleaguePickerSingle: React.FC<SingleProps> = ({
  value, onChange, label, placeholder, helperText, required, size = 'small',
  disabled, excludeIds, includeSelf = false,
}) => {
  const { t } = useTranslation();
  const { inputValue, setInputValue, options, loading } = useColleagueSearch(includeSelf);
  const visibleOptions = useMemo(() => {
    if (!excludeIds || excludeIds.length === 0) return options;
    const set = new Set(excludeIds);
    return options.filter((o) => !set.has(o.worker_id));
  }, [options, excludeIds]);

  return (
    <Autocomplete<MeColleague, false, false, false>
      options={visibleOptions}
      value={value}
      onChange={(_, v) => onChange(v)}
      onInputChange={(_, v) => setInputValue(v)}
      getOptionLabel={labelOf}
      isOptionEqualToValue={eqOf}
      filterOptions={(x) => x}
      loading={loading}
      disabled={disabled}
      size={size}
      noOptionsText={inputValue.trim() ? t('common:colleaguePicker.noMatches') : t('common:colleaguePicker.typeToSearch')}
      renderOption={renderColleagueOption}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder ?? t('common:colleaguePicker.searchPlaceholder')}
          required={required}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

/**
 * Multi-colleague picker (CC field use case). Renders selected
 * colleagues as compact chips; chip color is the default to keep the
 * field visually quiet within a dense form.
 */
export const ColleaguePickerMulti: React.FC<MultiProps> = ({
  value, onChange, label, placeholder, helperText, required, size = 'small',
  disabled, excludeIds, includeSelf = false,
}) => {
  const { t } = useTranslation();
  const { inputValue, setInputValue, options, loading } = useColleagueSearch(includeSelf);
  const visibleOptions = useMemo(() => {
    if (!excludeIds || excludeIds.length === 0) return options;
    const set = new Set(excludeIds);
    return options.filter((o) => !set.has(o.worker_id));
  }, [options, excludeIds]);

  // Avoid showing already-selected options inside the dropdown
  // (Autocomplete does this by default but only when
  // isOptionEqualToValue can match a freshly-fetched option to the
  // current value — make sure that comparison stays cheap).
  const handleChange = useCallback(
    (_: unknown, v: MeColleague[]) => onChange(v),
    [onChange],
  );

  return (
    <Autocomplete<MeColleague, true, false, false>
      multiple
      options={visibleOptions}
      value={value}
      onChange={handleChange}
      onInputChange={(_, v) => setInputValue(v)}
      getOptionLabel={labelOf}
      isOptionEqualToValue={eqOf}
      filterOptions={(x) => x}
      loading={loading}
      disabled={disabled}
      size={size}
      noOptionsText={inputValue.trim() ? t('common:colleaguePicker.noMatches') : t('common:colleaguePicker.typeToSearch')}
      renderOption={renderColleagueOption}
      renderTags={(picked, getTagProps) =>
        picked.map((opt, idx) => {
          // Pull the auto-injected key out — React 18 warns on key
          // spread, MUI's getTagProps puts key in the returned object.
          const { key, ...tagProps } = getTagProps({ index: idx });
          return (
            <Chip
              {...tagProps}
              key={key ?? opt.worker_id}
              size="small"
              label={opt.name}
              avatar={
                opt.avatar_url ? (
                  <Avatar src={opt.avatar_url} alt={opt.name} />
                ) : undefined
              }
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length === 0 ? placeholder ?? t('common:colleaguePicker.searchPlaceholder') : ''}
          required={required}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};
